import { type DependencyList, type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";

/**
 * Options for the {@link useData} hook.
 */
export interface UseDataOptions {
  /**
   * If `true`, the previous data value is preserved while the new one is being fetched.
   * Otherwise, the data is reset to `null` before the callback runs.
   */
  persist?: boolean;
}

/**
 * Runs an async `callback` whenever one of the `deps` changes and stores the resolved value in state.
 *
 * If the effect is cleaned up (e.g., because the component unmounted or the dependencies changed)
 * before the promise resolves, the result is discarded to avoid updating state on a stale hook.
 *
 * @param callback Function returning a promise with the data to store.
 * @param deps Dependency list passed to the underlying `useEffect`.
 * @param options Additional options controlling the hook's behavior.
 * @returns A tuple of the current data and a setter to update it manually.
 */
export function useData<T>(
  callback: () => Promise<T>,
  deps: DependencyList = [],
  options?: UseDataOptions,
): [T | null | undefined, Dispatch<SetStateAction<T | null | undefined>>] {
  const [data, setData] = useState<T | null | undefined>();

  useEffect(() => {
    let cancelled = false;
    if (options?.persist !== true) {
      setData(null);
    }

    callback().then(data => {
      if (!cancelled) {
        setData(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, deps);

  return [data, setData];
}

/**
 * Attaches an event listener to the given `element` for the lifetime of the component,
 * automatically removing it on cleanup or when any of the arguments change.
 *
 * @param element The DOM element (or any `EventTarget`) to attach the listener to.
 * @param eventName Name of the event to listen for.
 * @param callback Handler invoked when the event fires.
 */
export function useEventListener<K extends keyof HTMLElementEventMap>(
  element: EventTarget,
  eventName: K,
  callback: (event: HTMLElementEventMap[K]) => void,
): void;
export function useEventListener(element: EventTarget, eventName: string, callback: EventListenerOrEventListenerObject): void;
export function useEventListener(element: EventTarget, eventName: string, callback: EventListenerOrEventListenerObject): void {
  useEffect(() => {
    element.addEventListener(eventName, callback);
    return () => element.removeEventListener(eventName, callback);
  }, [element, eventName, callback]);
}

/**
 * Manages a single open/close dropdown identified by a string key.
 * Clicking anywhere on `document.body` closes the dropdown.
 *
 * @returns A tuple of the active dropdown key (or `undefined`) and a toggle handler.
 */
export function useDropdown(): [string | undefined, (key: string, element: EventTarget) => void] {
  const [open, setOpen] = useState<string | undefined>(undefined);
  const triggerRef = useRef<EventTarget | null>(null);

  const toggle = useCallback((key: string, element: EventTarget) => {
    triggerRef.current = element;
    setOpen(prev => (prev === key ? undefined : key));
  }, []);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (triggerRef.current && (triggerRef.current as Node).contains(e.target as Node)) return;
      setOpen(undefined);
    };
    document.body.addEventListener("click", close);
    return () => document.body.removeEventListener("click", close);
  }, []);

  return [open, toggle];
}

/**
 * Returns a monotonically increasing version number that ticks up every `interval` milliseconds.
 *
 * Useful for forcing components to re-render on a timer without storing the current time in state.
 *
 * @param interval Interval between version bumps, in milliseconds.
 * @returns The current version number, starting at `0`.
 */
export function useVersionedTimer(interval: number): number {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setVersion(version + 1), interval);

    return () => clearTimeout(timer);
  }, [version, interval]);

  return version;
}
